import React, { createContext, useState, useContext, useEffect } from "react"
import Cookies from "js-cookie"
import { useHistory } from "react-router-dom"
import api from "../utils/api"
import { ErrorNotification, SuccessNotification } from "../utils"
import axios from "axios"
import { BASE_URL } from "../utils/api"
import { usePermissions } from "../hooks/usePermissions"
import { isManagedUserAccount, withManagedUserContext, rejectInactiveAccount, isAccountActive, INACTIVE_ACCOUNT_MESSAGE } from "../utils/accessControl"
import { fetchSenderIdPromptState } from "../utils/smsSenderId"
import SessionExpiredModal from "../components/modals/SessionExpiredModal"
import CommoditySelectionModal from "../components/modals/CommoditySelectionModal"

const AuthContext = createContext({})

/** True when login / users/me says commodity selection is still required. */
export const isCommoditySelectionRequired = (userOrPayload) => {
  if (!userOrPayload) return false
  if (userOrPayload.commodity_selection_required === true) return true
  if (userOrPayload.user?.commodity_selection_required === true) return true
  return false
}

export const AuthProvider = ({ children }) => {
  const router = useHistory()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [redirectToReferrer, setRedirectToReferrer] = useState(false)
  const [isfirstTime, setFirstTime] = useState(false)
  const [isAuthenticated, setisAuthenticated] = useState(false)
  const [allusers, setUsers] = useState()
  const [analyticData, setAnalyticData] = useState()
  const [userType, setUserType] = useState("")
  const [passwordloading, setPasswordLoading] = useState(false)
  const [resetpasswordloading, setResetPasswordLoading] = useState(false)
  const [otpLoading, setOtpLoading] = useState(false)
  const [showSmsModal, setShowSmsModal] = useState(false)
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false)
  const [showCommodityModal, setShowCommodityModal] = useState(false)

  const permissions = usePermissions(user)

  const syncCommoditySelectionModal = (userOrPayload) => {
    const required = isCommoditySelectionRequired(userOrPayload)
    setShowCommodityModal(required)
    return required
  }

  const persistAuthenticatedUser = (nextUser) => {
    if (!nextUser) return null
    window.localStorage.setItem("user", JSON.stringify(nextUser))
    setUser(nextUser)
    syncCommoditySelectionModal(nextUser)
    return nextUser
  }

  const clearAuthSession = () => {
    Cookies.remove("token")
    setUser(null)
    delete api.defaults.headers.Authorization
    const theme = window.localStorage.getItem("theme")
    window.localStorage.clear()
    if (theme) {
      window.localStorage.setItem("theme", theme)
    }
    window.localStorage.removeItem("is_impersonation")
    window.localStorage.removeItem("impersonation_start_time")
    window.localStorage.removeItem("session_id")
    setRedirectToReferrer(false)
    setisAuthenticated(false)
    window.location.pathname = "/"
  }

  const logoutInactiveAccount = () => {
    ErrorNotification(INACTIVE_ACCOUNT_MESSAGE)
    clearAuthSession()
  }

  const fetchCurrentUserProfile = async () => {
    const { data } = await api.get('users/me/')
    return data
  }

  const ensureAccountIsActive = async (cachedUser = null) => {
    try {
      const freshUser = await fetchCurrentUserProfile()
      if (!isAccountActive(freshUser)) {
        logoutInactiveAccount()
        return { active: false, user: freshUser }
      }
      return { active: true, user: freshUser }
    } catch (error) {
      if (cachedUser && !isAccountActive(cachedUser)) {
        logoutInactiveAccount()
        return { active: false, user: cachedUser }
      }
      return { active: true, user: cachedUser }
    }
  }

  const enrichManagedUserPermissions = async (rawUser, loginPayload = null) => {
    if (!rawUser) return rawUser

    const managedFlag = isManagedUserAccount(rawUser, loginPayload)
    if (!managedFlag || !rawUser?.id) return rawUser

    try {
      const permissionsResponse = await api.get(`managed-users/${rawUser.id}/user_permissions`)
      return withManagedUserContext({
        ...rawUser,
        managed_permissions: permissionsResponse.data,
      }, loginPayload)
    } catch (error) {
      console.error('Failed to fetch managed user permissions:', error)
      return withManagedUserContext(rawUser, loginPayload)
    }
  }

  const handleImpersonationLogin = async (urlParams) => {
    try {
      // Extract tokens from URL
      const accessToken = urlParams.get('access_token')
      const refreshToken = urlParams.get('refresh_token')
      const sessionId = urlParams.get('session_id')

      const userDataString = urlParams.get('user_data')
      let userData = null

      if (userDataString) {
        try {
          userData = JSON.parse(decodeURIComponent(userDataString))
        } catch (error) {
          console.error('Failed to parse user_data from URL:', error)
          ErrorNotification("Failed to parse user data")
          setLoading(false)
          return
        }
      }

      if (!accessToken || !refreshToken || !userData) {
        console.error('Missing required impersonation data')
        ErrorNotification("Missing required impersonation data")
        setLoading(false)
        return
      }

      // Save data exactly like signin does (without the API post)
      window.localStorage.setItem("email", userData?.email || urlParams.get('email') || '')

      Cookies.set("token", accessToken, { expires: 60 })
      api.defaults.headers.Authorization = `Bearer ${accessToken}`

      let finalUserData = userData
      if (userData?.is_activity_user || userData?.user_type === 'activity_user' || userData?.activity_role) {
        try {
          const activityRoleResponse = await api.get(`activity-users/${userData.id}/activity-role/`)
          finalUserData = {
            ...userData,
            activity_role: activityRoleResponse.data.activity_role
          }
        } catch (error) {
          console.error('Failed to fetch activity role during impersonation:', error)
        }
      }

      finalUserData = await enrichManagedUserPermissions(finalUserData)

      window.localStorage.setItem("user", JSON.stringify(finalUserData))
      window.localStorage.setItem("token", accessToken)
      window.localStorage.setItem("refresh_token", refreshToken)
      window.localStorage.setItem("is_firsttime", userData?.is_approved || urlParams.get('is_firsttime') === 'true' || false)
      window.localStorage.setItem("verification_required", urlParams.get('verification_required') === 'true' || false)
      window.localStorage.setItem("is_impersonation", "true")
      // Store impersonation start time for banner timer
      window.localStorage.setItem("impersonation_start_time", Date.now().toString())
      if (sessionId) {
        window.localStorage.setItem("session_id", sessionId)
      }

      setUser(finalUserData)
      syncCommoditySelectionModal(finalUserData)
      setFirstTime(userData.is_approved || false)
      setUserType(userData.user_type || '')
      setRedirectToReferrer(true)
      setisAuthenticated(true)
      setLoading(false)

      window.history.replaceState({}, '', window.location.pathname)

      router.push("/admin/dashboard")
      SuccessNotification("Impersonation session started successfully")

    } catch (error) {
      console.error('Impersonation login failed:', error)
      ErrorNotification("Impersonation login failed")
      setLoading(false)
    }
  }

  async function loadUserFromCookies() {
    // Don't load from cookies if this is an impersonation session
    const isImpersonation = window.localStorage.getItem("is_impersonation") === "true"
    if (isImpersonation) {
      setLoading(false)
      return
    }

    const token = Cookies.get("token")
    const user = window.localStorage.getItem("user")
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`
      if (user) {
        const data = JSON.parse(user)
        const { active, user: freshUser } = await ensureAccountIsActive(data)
        if (!active) {
          setLoading(false)
          return
        }

        const mergedUser = freshUser ? { ...data, ...freshUser } : data

        const isDismissed = sessionStorage.getItem("sms_modal_dismissed") === "true"
        const { shouldShow: shouldShowModal } = await fetchSenderIdPromptState(mergedUser)

        // Check if user is an activity user and fetch their role
        if (mergedUser?.is_activity_user || mergedUser?.user_type === 'activity_user' || mergedUser?.activity_role) {
          try {
            const activityRoleResponse = await api.get(`activity-users/${mergedUser.id}/activity-role/`)
            const userWithRole = {
              ...mergedUser,
              activity_role: activityRoleResponse.data.activity_role
            }
            const managedUserWithPermissions = await enrichManagedUserPermissions(userWithRole)
            persistAuthenticatedUser(managedUserWithPermissions)
          } catch (error) {
            console.error('Failed to fetch activity role:', error)
            const managedUserWithPermissions = await enrichManagedUserPermissions(mergedUser)
            persistAuthenticatedUser(managedUserWithPermissions)
          }
        } else {
          const managedUserWithPermissions = await enrichManagedUserPermissions(mergedUser)
          persistAuthenticatedUser(managedUserWithPermissions)
        }

        setRedirectToReferrer(true)
        setisAuthenticated(true)
        setShowSmsModal(shouldShowModal)
      }
    }
    setLoading(false)
  }

  // Check URL parameters for impersonation on mount
  useEffect(() => {
    const handleSessionExpired = () => {
      const token = Cookies.get('token') || window.localStorage.getItem('token')
      const publicPaths = ['/forgot-password', '/reset-password', '/signup', '/2fa', '/confirmation', '/complete-invite', '/activity-user']
      const isPublicAuthPage = publicPaths.some((path) => window.location.pathname.startsWith(path))
      const isLoginPage = window.location.pathname === '/'

      if (!token || token === 'null' || isPublicAuthPage || isLoginPage) return
      setShowSessionExpiredModal(true)
    }

    window.addEventListener('session-expired', handleSessionExpired)

    const urlParams = new URLSearchParams(window.location.search)
    const isImpersonation = urlParams.get('impersonation') === 'true'

    if (isImpersonation) {
      console.log('Impersonation detected in URL')
      handleImpersonationLogin(urlParams)
    } else {
    // Not impersonating, load normally from cookies
      loadUserFromCookies()
    }

    return () => window.removeEventListener('session-expired', handleSessionExpired)
  }, [])

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return undefined

    const checkActiveStatus = async () => {
      const { active, user: freshUser } = await ensureAccountIsActive(user)
      if (!active || !freshUser) return

      setUser((currentUser) => {
        const updatedUser = { ...currentUser, ...freshUser }
        window.localStorage.setItem("user", JSON.stringify(updatedUser))
        syncCommoditySelectionModal(updatedUser)
        return updatedUser
      })
    }

    checkActiveStatus()
    const intervalId = window.setInterval(checkActiveStatus, 60000)
    window.addEventListener('focus', checkActiveStatus)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('focus', checkActiveStatus)
    }
  }, [isAuthenticated, user?.id])

  const signin = async (params) => {
    try {
      var appdata = {
        password: params.password,
        email: params.email,
      }

      const { data } = await axios.post(`${BASE_URL}auth/login/`, appdata)

      if (data?.user?.id) {
        if (rejectInactiveAccount(data.user, ErrorNotification)) {
          return
        }

        window.localStorage.setItem("email", data?.user?.email)
        // updated code
        if (data?.verification_required || data?.user?.is_approved) {
          Cookies.set("token", data.tokens?.access, { expires: 60 })
          api.defaults.headers.Authorization = `Bearer ${data.tokens?.access}`

          let userData = withManagedUserContext(data.user, data)
          if (data.user?.is_activity_user || data.user?.user_type === 'activity_user' || data.user?.activity_role) {
            try {
              const activityRoleResponse = await api.get(`activity-users/${data.user.id}/activity-role/`)
              userData = {
                ...userData,
                activity_role: activityRoleResponse.data.activity_role,
              }
            } catch (error) {
              console.error('Failed to fetch activity role during login:', error)
            }
          }

          userData = await enrichManagedUserPermissions(userData, data)
          // Prefer top-level login flag when present
          if (typeof data.commodity_selection_required === 'boolean') {
            userData = {
              ...userData,
              commodity_selection_required: data.commodity_selection_required,
            }
          }

          window.localStorage.setItem("user", JSON.stringify(userData))
          window.localStorage.setItem("token", data.tokens?.access)
          window.localStorage.setItem("refresh_token", data.tokens?.refresh)
          window.localStorage.setItem(
            "is_firsttime",
            data?.user?.is_firsttime ? "true" : "false"
          )
          window.localStorage.setItem(
            "verification_required",
            data?.verification_required ? "true" : "false"
          )
          setUser(userData)
          syncCommoditySelectionModal(data)
          setFirstTime(Boolean(data.user.is_firsttime))
          setUserType(data.user.user_type)
          setRedirectToReferrer(true)
          setisAuthenticated(true)
          router.push("/admin/dashboard")
        } else {
          window.location.href = "/confirmation"
        }
      } else if (data?.two_factor_required && data?.email) {
        window.localStorage.setItem("email", JSON.stringify(data?.email))
        window.location.href = "/2fa"
      } else {
        ErrorNotification(data?.message || "Wrong email and password")
      }
    } catch (error) {
      if (error.response.data.approval_pending) {
        window.location.href = "/confirmation"
      }
      throw new Error(error.response.data.error)
    }
  }

  const signup = async (params) => {
    try {
      const formdata = new FormData()
      formdata.append("community", params?.community)
      formdata.append("district", params?.district)
      formdata.append("dob", params?.dob)
      formdata.append("email", params?.email)
      formdata.append("firstname", params?.firstname)
      formdata.append("gender", params?.gender)
      formdata.append("ghana_card_number", params?.ghana_card_number)
      formdata.append("lastname", params?.lastname)
      formdata.append("password", params?.password)
      formdata.append("phone", params?.phone)
      formdata.append("region", params?.region)
      formdata.append("is_business_account", params?.is_business_account)

      formdata.append("user_type", params?.user_type)
      params?.factory_size && formdata.append("factory_size", params?.factory_size)

      // Handle materials_source as an array
      if (params?.materials_source) {
        if (typeof params.materials_source === 'string') {
          try {
            params.materials_source = JSON.parse(params.materials_source)
          } catch (e) {
            params.materials_source = [params.materials_source]
          }
        }
        if (Array.isArray(params.materials_source)) {
          params.materials_source.forEach(source => {
            formdata.append("materials_source", source)
          })
        }
      }

      if (params?.business_name) {
        formdata.append("business_name", params.business_name)
      }

      // Handle file uploads properly
      // Check and append doc_url file
      if (params?.doc_url) {
        if (params.doc_url instanceof File) {
          formdata.append("doc_url", params.doc_url)
          // filepath is the same as doc_url for business files
          if (params?.is_business_account) {
            formdata.append("filepath", params.doc_url)
          }
        } else if (typeof params.doc_url === 'string' && params.doc_url.trim() !== '') {
          // If it's a string (file path), append it as is
          formdata.append("doc_url", params.doc_url)
          if (params?.is_business_account) {
            formdata.append("filepath", params.doc_url)
          }
        }
      }

      // Check and append ghana_card_image file
      if (params?.ghana_card_image) {
        if (params.ghana_card_image instanceof File) {
          formdata.append("ghana_card_image", params.ghana_card_image)
        } else if (typeof params.ghana_card_image === 'string' && params.ghana_card_image.trim() !== '') {
          // If it's a string (file path), append it as is
          formdata.append("ghana_card_image", params.ghana_card_image)
        }
      }

      // Also handle filepath if set separately (for non-business accounts)
      if (params?.filepath) {
        if (params.filepath instanceof File && !params?.is_business_account) {
          formdata.append("filepath", params.filepath)
        } else if (typeof params.filepath === 'string' && params.filepath.trim() !== '' && !params?.is_business_account) {
          formdata.append("filepath", params.filepath)
        }
      }

      const { data } = await axios.post(`${BASE_URL}auth/register/`, formdata)

      if (data?.user) {
        window.localStorage.setItem("email", data?.user?.email)
        SuccessNotification("User account created successfully")
        window.location.href = "/confirmation"
      }
    } catch (error) {
      ErrorNotification(error.response.data || error.message)
      // throw new Error(error.response.data.error)
    }
  }

  const forgotPassword = async (payload) => {
    try {
      setPasswordLoading(true)
      const { data } = await axios.post(`${BASE_URL}auth/requestpasswordreset/`, payload)
      SuccessNotification(data.success)
      setPasswordLoading(false)
      // window.location.href="/reset-password"
    } catch (error) {
      setPasswordLoading(false)
      ErrorNotification(error.response.data.error)
    }
  }

  const updatePassword = async (payload) => {
    setLoading(true)
    try {
      const resp = await axios.post(
        `${BASE_URL}auth/updatepassword/`,
        JSON.stringify(payload)
      )
      localStorage.setItem("token", resp.new_token)
      setLoading(false)
      SuccessNotification("Password has been updated successfully")
      setTimeout(() => {
        logout()
      }, 1500)
    } catch (error) {
      ErrorNotification("There was an error updating password")
      setLoading(false)

    }
  }

  const resetPassword = async (payload) => {
    setResetPasswordLoading(true)
    try {
      const resp = await api.post(
        "auth/resetpassword/",
        JSON.stringify(payload)
      )
      localStorage.setItem("token", resp.new_token)
      setResetPasswordLoading(false)
      SuccessNotification("Password has been updated successfully")
    } catch (error) {
      ErrorNotification("There was an error updating password")
      setResetPasswordLoading(false)

    }
  }

  const resendCode = async (payload) => {
    try {
      return await api.post("auth/requestpasswordcode/", payload)
    } catch (error) {

    }
  }

  const getUserDetails = async (id) => {
    try {
      const user = await api.get(`auth/getUserDetails/${id}/`)
      return user
    } catch (error) {

    }
  }

  /**
   * First-time / login OTP resend.
   * Uses public POST auth/resend-otp/ with email (no token required).
   * Do not use legacy auth/sendotp/ for this flow.
   */
  const resendOtp = async (emailOverride) => {
    const storedUser = (() => {
      try {
        return JSON.parse(window.localStorage.getItem("user") || "null")
      } catch {
        return null
      }
    })()
    const email =
      emailOverride ||
      user?.email ||
      storedUser?.email ||
      window.localStorage.getItem("email")

    const normalizedEmail =
      typeof email === "string" ? email.replace(/^"|"$/g, "").trim() : ""

    if (!normalizedEmail || normalizedEmail === "N/A") {
      ErrorNotification("Email is required to resend the verification code")
      throw new Error("Email is required")
    }

    try {
      const { data } = await axios.post(`${BASE_URL}auth/resend-otp/`, {
        email: normalizedEmail,
      })
      SuccessNotification(
        data?.message ||
          "A new verification code was sent. Use the latest code from your email or SMS."
      )
      return data
    } catch (error) {
      const status = error?.response?.status
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        (status === 403
          ? "Your account is pending approval. OTP cannot be resent at this time."
          : status === 404
            ? "User not found for that email."
            : "There was an error resending the verification code")
      ErrorNotification(message)
      throw error
    }
  }

  /** @deprecated Prefer resendOtp for first-time verification */
  const createSms = async () => resendOtp()

  const VerifySms = async (otp) => {
    const data = JSON.stringify({
      otp_code: String(otp || "").trim(),
    })
    try {
      setOtpLoading(true)
      const response = await api.post("auth/verify-otp/", data)
      if (response?.data?.user) {
        if (rejectInactiveAccount(response.data.user, ErrorNotification)) {
          return
        }

        let userData = withManagedUserContext(response.data.user, response.data)
        userData = await enrichManagedUserPermissions(userData, response.data)
        if (typeof response.data.commodity_selection_required === 'boolean') {
          userData = {
            ...userData,
            commodity_selection_required: response.data.commodity_selection_required,
          }
        }

        // Account is verified after a successful OTP check.
        userData = {
          ...userData,
          is_firsttime: false,
        }

        localStorage.setItem("user", JSON.stringify(userData))
        localStorage.setItem("is_firsttime", "false")
        localStorage.setItem("verification_required", "false")
        setUser(userData)
        setFirstTime(false)
        syncCommoditySelectionModal(response.data)
        setRedirectToReferrer(true)
        setisAuthenticated(true)

        const { shouldShow } = await fetchSenderIdPromptState(userData)
        setShowSmsModal(shouldShow)

        SuccessNotification(
          response?.data?.message ||
            response?.message ||
            "Account verified successfully"
        )
        return userData
      } else {
        ErrorNotification(
          response?.data?.message ||
            response?.message ||
            "There was an error verifying the code"
        )
      }
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Invalid or expired OTP. Request a new code and try again."
      ErrorNotification(message)
      throw error
    } finally {
      setOtpLoading(false)
    }
  }

  const refreshActivityRole = async () => {
    if (!user?.id) return null

    try {
      const activityRoleResponse = await api.get(`activity-users/${user.id}/activity-role/`)
      const userWithRole = {
        ...user,
        activity_role: activityRoleResponse.data.activity_role
      }
      setUser(userWithRole)
      window.localStorage.setItem("user", JSON.stringify(userWithRole))
      return userWithRole
    } catch (error) {
      console.error('Failed to refresh activity role:', error)
      return null
    }
  }

  const saveTradingCommodities = async (commodities) => {
    const { data } = await api.patch('users/me/', {
      trading_commodities: commodities,
    })

    const updatedUser = {
      ...user,
      ...data,
      trading_commodities: data?.trading_commodities ?? commodities,
      commodity_selection_required:
        typeof data?.commodity_selection_required === 'boolean'
          ? data.commodity_selection_required
          : false,
    }

    persistAuthenticatedUser(updatedUser)
    return updatedUser
  }

  const logout = () => {
    setShowCommodityModal(false)
    clearAuthSession()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isfirstTime,
        signin,
        setUser,
        analyticData,
        loading,
        logout,
        signup,
        forgotPassword,
        userType,
        updatePassword,
        redirectToReferrer,
        setRedirectToReferrer,
        setisAuthenticated,
        isAuthenticated,
        showSmsModal,
        createSms,
        resendOtp,
        setUserType,
        resendCode,
        setFirstTime,
        VerifySms,
        getUserDetails,
        forgotPassword,
        passwordloading,
        resetpasswordloading,
        setShowSmsModal,
        resetPassword,
        loadUserFromCookies,
        otpLoading,
        permissions, // Add permissions to context
        refreshActivityRole, // Add refresh function
        showCommodityModal,
        setShowCommodityModal,
        syncCommoditySelectionModal,
        saveTradingCommodities,
      }}
    >
      {showSessionExpiredModal && (
        <SessionExpiredModal handleLogout={() => {
          setShowSessionExpiredModal(false)
          logout()
        }} />
      )}
      {isAuthenticated && showCommodityModal && (
        <CommoditySelectionModal onSubmit={saveTradingCommodities} />
      )}
      {children}
    </AuthContext.Provider>
  )
}

export default function useAuth() {
  return useContext(AuthContext)
}